import { DealRole } from '__generated__/globalTypes';
// Utility function to convert basis points to percentage
const basisPointsToPercent = (n: number | null) =>
  n || n === 0 ? ((n / 10000 + 1) * 100).toFixed(3) : '';

export interface BidData {
  bid_id: string;
  bidder: DealRole;
  bid_basis_points: number | null;
  created_date: string;
  prior_bid_id: string | null;
  carve_name: string;
  carve?: Array<{
    field_name: string;
    operator: string;
    operand: string | null;
  }> | null;
  carve_summary?: any;
  price?: any;
  stipulations?: any;
  terms?: any;
  status: string;
}

// Type adapter to convert GraphQL bid types to our BidData interface
export const adaptBidCardBid = (bid: any): BidData => ({
  bid_id: bid.bid_id,
  bidder: bid.bidder,
  bid_basis_points: bid.bid_basis_points,
  created_date: bid.created_date, // GraphQL DateTime will be converted to string
  prior_bid_id: bid.prior_bid_id,
  carve_name: bid.carve_name,
  carve:
    bid.carve?.map((c: any) => ({
      field_name: c.field_name,
      operator: c.operator,
      operand: c.operand || '',
    })) || null,
  carve_summary: bid.carve_summary,
  price: bid.price,
  stipulations: bid.stipulations,
  terms: bid.terms,
  status: bid.status,
});

// Type adapter for BiddingAndPricing bid types
export const adaptPlaceBidCardBid = (bid: any): BidData => ({
  bid_id: bid.bid_id,
  bidder: bid.bidder,
  bid_basis_points: bid.bid_basis_points,
  created_date: bid.created_date,
  prior_bid_id: bid.prior_bid_id,
  carve_name: bid.carve_name,
  carve:
    bid.carve?.map((c: any) => ({
      field_name: c.field_name,
      operator: c.operator,
      operand: c.operand || '',
    })) || null,
  carve_summary: bid.carve_summary,
  price: bid.price,
  stipulations: bid.stipulations,
  terms: bid.terms,
  status: bid.status,
});

/**
 * Generates a descriptive label for a bid based on its carve name from the backend
 */
export const getBidDescription = (bid: BidData): string => {
  return bid.carve_name;
};

/**
 * Gets the display name for a bid (Bid vs Counteroffer)
 */
export const getBidTypePrefix = (bid: BidData, isCounterOffer: boolean): string => {
  return isCounterOffer ? 'Counteroffer' : 'Bid';
};

/**
 * Formats bid price as percentage
 */
export const formatBidPrice = (bid: BidData): string => {
  const basisPoints = bid.bid_basis_points ?? 0;
  return `${basisPointsToPercent(basisPoints)}%`;
};

/**
 * Returns a short ownership label for display in bid rows.
 * Shows "Participation (X%)" for fractional ownership or null for whole loan.
 */
export const getOwnershipBadge = (bid: BidData): string | null => {
  const transferAmount = Number(
    bid.price?.transfer_amount ?? bid.stipulations?.transfer_amount ?? 100,
  );
  return transferAmount < 100 ? `Participation (${transferAmount}%)` : null;
};

/**
 * Returns ownership label including "Whole Loan" for 100% trades.
 */
export const getOwnershipLabel = (bid: BidData): string => {
  return getOwnershipBadge(bid) ?? 'Whole Loan';
};

/**
 * Same format as getOwnershipLabel but from a participation rate (e.g. listing.participation_rate).
 * Use for Offer Details / summaries where the source is a rate number rather than a bid.
 */
export const getParticipationLabelFromRate = (rate: number | null | undefined): string => {
  if (rate == null || rate >= 100) return 'Whole Loan';
  return `Participation (${rate}%)`;
};

/**
 * Finds all counteroffers for a given buyer bid
 */
export const findCounterOffers = (bids: BidData[], buyerBidId: string): BidData[] => {
  return bids.filter((bid) => bid.bidder === DealRole.SELLER && bid.prior_bid_id === buyerBidId);
};

/**
 * Groups bids into buyer bids with their counteroffers
 */
export const groupBidsByBuyer = (bids: BidData[]) => {
  const buyerBids = bids.filter((bid) => bid.bidder === DealRole.BUYER);

  return buyerBids.map((buyerBid) => ({
    buyerBid,
    counterOffers: findCounterOffers(bids, buyerBid.bid_id),
  }));
};

/**
 * Finds the seller counteroffer for a specific buyer bid
 */
export const findSellerCounteroffer = (
  bids: BidData[],
  buyerBidId: string,
): BidData | undefined => {
  return bids.find((bid) => bid.bidder === DealRole.SELLER && bid.prior_bid_id === buyerBidId);
};
